import {
  BOARD_WAITING_REASON_LABELS,
  type TicketWaitingPanel,
} from '@nightshift/ui-types';

import { Markdown } from '../../components/markdown.tsx';
import { DRAWER_ACTION_LABEL, type DrawerAction } from '../../lib/drawer-actions.ts';
import { splitWaitingReason } from '../../lib/waiting-reason.ts';

/**
 * Waiting is a typed halt, not a tracker reply box: the kind of wait, one short reason, and the
 * actions — amber only because a person must look.
 *
 * Deliberately nothing else. The line naming who was notified, whether it wakes by itself and
 * which state it resumes at, and the "Before resuming" restatement of the prerequisite, were
 * removed on 2026-09-26: they repeated what the reason and the buttons already say and made a
 * one-line question read as a form. A resume the prerequisite refuses is still not offered.
 */
export function WaitingPanel({
  waiting,
  actions,
  busy,
  onAction,
}: {
  waiting: TicketWaitingPanel;
  actions: DrawerAction[];
  busy: boolean;
  onAction: (action: DrawerAction) => void;
}) {
  const label = BOARD_WAITING_REASON_LABELS[waiting.reasonType] ?? waiting.reasonType;
  // The breaker appends the last attempt's `failure_detail` to the reason. It is the machine's
  // own words -- SHAs, paths, a push refusal -- so it keeps its line breaks and is not re-flowed
  // as Markdown prose beside the sentence that introduces it.
  const { narrative, detail } = splitWaitingReason(waiting.reason);
  const prerequisite = waiting.resumePrerequisite;
  const waitingActions = actions.filter(
    (action) => action !== 'reset-budget' && action !== 'shelve' && (action !== 'resume' || prerequisite.ok || prerequisite.overridable),
  );

  return (
    <section className="drawer-waiting" aria-label="Waiting">
      <div className="drawer-waiting-head">
        <span className="lamp lamp-breathe" aria-hidden />
        <span className="drawer-waiting-kicker">Waiting · {label}</span>
      </div>
      <div className="drawer-waiting-reason">
        <Markdown source={narrative} tight />
      </div>
      {detail ? (
        <div className="drawer-waiting-detail" aria-label="Last failure detail">
          {detail}
        </div>
      ) : null}
      {waiting.unknownAllowedActions.length > 0 ? (
        <p className="drawer-waiting-unknown">
          Unrecognised allowed action: {waiting.unknownAllowedActions.join(', ')}
        </p>
      ) : null}
      <div className="drawer-waiting-actions">
        {waitingActions.map((action) => (
          <button
            key={action}
            type="button"
            className={action === 'cancel' ? 'btn btn-danger btn-sm' : 'btn btn-accent btn-sm'}
            disabled={busy}
            onClick={() => onAction(action)}
          >
            {action === 'resume' && !prerequisite.ok ? 'Resume anyway' : DRAWER_ACTION_LABEL[action]}
          </button>
        ))}
      </div>
    </section>
  );
}
